import { supabase } from '../lib/supabase';
import { PLANS } from '../config/plans';

const FALLBACK_USAGE = {
  analyses: { used: 0, limit: PLANS.trial.analysisLimit },
  scenarios: { used: 0, limit: 0 },
  aiImports: { used: 0, limit: PLANS.trial.analysisLimit },
  aiAnalyses: { used: 0, limit: PLANS.trial.analysisLimit }
};

const usageTrackingService = {
  /**
   * Get current usage statistics for a user
   * Uses PostgreSQL function for reliable backend calculation
   */
  async getUsageStats(userId) {
    if (!userId) {
      return FALLBACK_USAGE;
    }

    try {
      // Call PostgreSQL function to get usage stats
      // The function automatically uses the user's subscription_tier from user_profiles
      //console.log('[UsageTracking] Calling get_user_usage_stats for userId:', userId);
      const { data, error } = await supabase.rpc('get_user_usage_stats', {
        p_user_id: userId
      });

      //console.log('[UsageTracking] RPC response - data:', JSON.stringify(data, null, 2), 'error:', error);

      if (error) {
        //console.error('Error fetching usage stats from backend:', error);
        // If function doesn't exist, return a helpful error message
        if (error.code === '42883' || error.message?.includes('does not exist')) {
          //console.error('Function get_user_usage_stats does not exist. Please run the migration SQL.');
        }
        // Fallback to default values
        return FALLBACK_USAGE;
      }

      // Parse the JSON response
      if (data && typeof data === 'object') {
        // Handle limit values - can be number or '∞' string
        const getLimit = (limit) => {
          if (limit === '∞' || limit === Infinity) return '∞';
          if (typeof limit === 'number') return limit;
          if (typeof limit === 'string' && limit === '∞') return '∞';
          // Try to parse as number if it's a string
          if (typeof limit === 'string') {
            const parsed = parseInt(limit, 10);
            if (!isNaN(parsed)) return parsed;
          }
          return limit || 0;
        };
        
        const getUsed = (used) => {
          if (typeof used === 'number') return used;
          if (typeof used === 'string') {
            const parsed = parseInt(used, 10);
            if (!isNaN(parsed)) return parsed;
          }
          return 0;
        };
        
        const result = {
          analyses: {
            used: getUsed(data.analyses?.used),
            limit: getLimit(data.analyses?.limit)
          },
          scenarios: {
            used: getUsed(data.scenarios?.used),
            limit: getLimit(data.scenarios?.limit)
          },
          aiImports: {
            used: getUsed(data.aiImports?.used),
            limit: getLimit(data.aiImports?.limit)
          },
          aiAnalyses: {
            used: getUsed(data.aiAnalyses?.used),
            limit: getLimit(data.aiAnalyses?.limit)
          }
        };
        //console.log('[UsageTracking] Parsed result:', JSON.stringify(result, null, 2));
        return result;
      }

      // Fallback if data format is unexpected
      return FALLBACK_USAGE;
    } catch (error) {
      //console.error('Error calculating usage stats:', error);
      return FALLBACK_USAGE;
    }
  }
};

export default usageTrackingService;
